<template>
    <v-card flat>
        <v-card-text class="pa-0">
            <p>
                <span class="font-weight-bold">Instance Creation with Latest Shared Content</span>
                <br />
                The instance(s) you will create will contain the most recent contents that you have shared so far (if any).
            </p>

            <v-tabs v-model="instanceInvitationMode" color="primary">
                <v-tab v-if="!isSpaceArchived">Invite via Link</v-tab>
                <v-tab>Invite via Email</v-tab>
                <v-tab-item v-if="!isSpaceArchived">
                    <div v-if="!invalidGenerator" class="mt-4">
                        <div v-if="instanceInvitationMode === instanceInvitationTabOptions.INVITE_VIA_LINK">
                            <v-alert type="info" text>
                                <b>Share the following link (e.g. via a Learning Management System)</b>
                                <br />
                                Participants will be able to sign up using the below link and receive their own separate instance for work
                            </v-alert>

                            <div class="d-flex justify-center">
                                <v-sheet color="grey lighten-3" class="d-flex align-center rounded py-2 px-4 w-100">
                                    <div class="mr-2">{{ firstInvGen }}</div>
                                    <v-spacer></v-spacer>
                                    <copy-to-clipboard
                                        :textToCopy="firstInvGen"
                                        buttonClass="caption font-weight-bold"
                                        buttonColor="primary"
                                        buttonName="copy to clipboard"
                                        :iconButton="true"
                                        :isSmall="true" />
                                </v-sheet>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <v-alert type="info" text class="mt-5">
                            <v-row align="center">
                                <v-col class="grow">No valid invitation generator was found. Please create a new generator to invite users.</v-col>
                                <v-col class="shrink">
                                    <v-btn :loading="creatingGenerator" @click="createInvitationGenerator" outlined small color="primary">
                                        Create invitation generator
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-alert>
                    </div>
                </v-tab-item>
                <v-tab-item>
                    <div class="mt-4">
                        <InviteEmailsInput
                            v-model="emailsInput"
                            :valid.sync="validMultipleInstances"
                            :disabled="inviting"
                            :showGenerateLinkOnlyToggle="true"
                            :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
                            :invitationErrorState="{
                                showDialog: showInvitationErrorsDialog,
                                errorType: validationErrorType,
                                invalidEmails: invalidEmailsList,
                                foundInvalidEmails,
                                backendError,
                                emailsValidationError
                            }"
                            @input="updateEmailString"
                            @proceedWithTheInvitation="inviteUsers($event.value)"
                            @inviteWithoutFetchingDistributed="createInstances(null, $event.value)"
                            @fetchAndInviteAgain="inviteUsers($event.value)" />

                        <v-dialog persistent v-model="showEmailWarning" max-width="500">
                            <v-card>
                                <v-card-title class="flex-nowrap align-start">
                                    <span class="secondary--text">Too many recipients</span>
                                    <v-spacer></v-spacer>
                                    <v-btn @click="showEmailWarning = false" icon><v-icon>clear</v-icon></v-btn>
                                </v-card-title>
                                <v-card-text>
                                    <p>You might want to consider inviting this many users via the invitation link instead:</p>
                                    <div class="d-flex justify-center align-center">
                                        <v-text-field :value="firstInvGen" :readonly="true" outlined></v-text-field>
                                        <CopyToClipboard
                                            :textToCopy="firstInvGen"
                                            buttonClass="caption font-weight-bold"
                                            buttonColor="primary"
                                            :isTextButton="true"
                                            buttonName="copy to clipboard"
                                            :iconButton="true"
                                            :isSmall="false" />
                                    </div>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>

                                    <v-btn @click="skipDialog" color="primary" text>Invite anyway</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </div>
                </v-tab-item>
            </v-tabs>
        </v-card-text>
        <v-card-actions v-if="instanceInvitationMode !== instanceInvitationTabOptions.INVITE_VIA_LINK && (showInviteAction || error)">
            <div class="d-flex flex-column align-center w-100">
                <v-btn
                    v-if="showInviteAction"
                    @click="validateEmailsAndInvite()"
                    :loading="inviting || validatingEmails"
                    :disabled="!validMultipleInstances || validatingEmails || inviting || !emailsInput"
                    color="primary"
                    class="mr-1">
                    <v-icon small>add</v-icon>
                    Users
                </v-btn>
                <v-alert v-if="error" color="error" icon="warning" class="mt-4 w-100" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Invitation Failure Error</span>
                        An error has occurred while inviting the users. Please try again and if the problem persists contact support@alphacruncher.com
                    </div>
                </v-alert>
            </div>
        </v-card-actions>
    </v-card>
</template>

<script>
import { splitEmails, sortArray, isDateGreaterThanToday, isCurrentState, isDistributedInstance } from '@/utils'
import { emailValidation } from '@/mixins/validateEmail'
import { mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(invitationsErrors, lifecycleErrors, permissionsErrors, snapshotErrors, spaceErrors)

const CopyToClipboard = () => import('@/components/CopyToClipboard')

export default {
    props: {
        showInviteAction: {
            type: Boolean,
            default: true
        },
        spaceId: {
            type: [Number, String],
            default: null
        },
        spaceArchived: {
            type: Boolean,
            default: null
        }
    },
    mixins: [enumsData, emailValidation],
    data() {
        return {
            instanceInvitationMode: 0,
            instanceInvitationTabOptions: {
                INVITE_VIA_EMAIL: 1,
                INVITE_VIA_LINK: 0
            },
            error: false,
            errorContent: 'An error has occurred',
            emailsInput: '',
            emailList: [],
            failedInvitations: [],
            inviting: false,
            validMultipleInstances: false,
            invgens: [],
            distributedFetchingError: false,
            DISTRIBUTED_SNAPSHOTS_FETCHING_ERROR: 'fetchingDistributedError',
            generateInvitationLinksOnly: false,
            creatingGenerator: false,
            invalidGenerator: false,
            showEmailWarning: false,
            invitationSourceRequest: 0
        }
    },
    components: {
        CopyToClipboard,
        InviteEmailsInput
    },
    methods: {
        emitInviteActionState() {
            const visible = this.instanceInvitationMode !== this.instanceInvitationTabOptions.INVITE_VIA_LINK
            this.$emit('invite-action-state', {
                visible,
                loading: this.inviting || this.validatingEmails,
                disabled: !visible || !this.validMultipleInstances || this.validatingEmails || this.inviting || !this.emailsInput
            })
        },
        triggerInviteFromParent() {
            this.validateEmailsAndInvite()
        },
        updateEmailString: function () {
            this.validatedEmails = []
            const emails = splitEmails(this.emailsInput)
            this.emailList = emails.map(email => email.email)
        },
        createInstances: function (latestDistributedSnapshotId, confirmed) {
            if (confirmed) {
                this.inviting = true
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Creating instance, please wait...',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'info'
                })
                this.error = false
                const postBody = { generate_link_only: this.generateInvitationLinksOnly }
                let apiURL = ''
                const emailsString = this.emailList.join(',')
                if (this.emailsInput) {
                    apiURL = '/spaces/' + this.resolvedSpaceId + '/create_individual_instances_async'
                    postBody.emails = emailsString
                }
                if (latestDistributedSnapshotId !== null) {
                    postBody.from_snid = latestDistributedSnapshotId
                }

                return this.$axios
                    .post(apiURL, postBody)
                    .then(response => {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'Instance creation has been started. Invited users will receive an email to accept the invitation.',
                            snackBarTimeout: 10000,
                            snackBarIcon: 'check_circle'
                        })
                        this.$emit('invite-success')
                        this.emailsInput = ''
                        this.$store.dispatch('spaceStore/fetchSpaceInstances', this.resolvedSpaceId)
                        this.$store.dispatch('spaceStore/fetchSpaceInvitations', this.resolvedSpaceId)
                    })
                    .catch(error => {
                        this.error = true
                        if (
                            error.response &&
                            error.response.data &&
                            error.response.data.reason &&
                            error.response.data.reason[0].includes('duplicate key value violates unique constraint "uc_sid_short"')
                        ) {
                            this.errorContent = 'Another instance uses the short name you chose for this instance, please choose another name'
                        } else if (
                            error.response &&
                            error.response.data &&
                            error.response.data.reason &&
                            error.response.data.reason[0].includes('duplicate key value violates unique constraint "uc_sid_long"')
                        ) {
                            this.errorContent =
                                'An instance associated with ' +
                                error.response.data.reason[0].match(/\d,.*/g)[0].replace(' already exists.', '').slice(3).replace(')', '') +
                                ' exists already'
                        } else {
                            const snackBarText = getBackendErrorMessage(
                                error,
                                'An error has occurred while sending the invitations, please try again and if the problem persists contact support@alphacruncher.com.'
                            )
                            this.error = !!snackBarText
                            if (snackBarText) {
                                this.$store.dispatch('showSnackBar', {
                                    snackBarText,
                                    snackBarTimeout: 10000,
                                    snackBarIcon: 'error'
                                })
                            }
                        }
                    })
                    .finally(() => {
                        this.inviting = false
                        this.generateInvitationLinksOnly = false
                    })
            }
        },
        async inviteUsers(confirmed) {
            if (!confirmed || this.inviting) return
            const sid = this.resolvedSpaceId
            const requestId = ++this.invitationSourceRequest
            const isCurrent = () => requestId === this.invitationSourceRequest && String(sid) === String(this.resolvedSpaceId)
            this.setEmailValidationDefaultData()
            this.inviting = true
            this.distributedFetchingError = false
            try {
                // Resolve from the invitation's space, not the route's shared store cache.
                const { data: instances } = await this.$axios.get(`spaces/${sid}/instances`)
                if (!isCurrent()) return
                const distributed = instances.find(instance => isDistributedInstance(instance.short_id || instance.long_id))
                let snapshotId = null
                if (distributed) {
                    const { data: snapshots } = await this.$axios.get(`instances/${distributed.iid}/snapshots`)
                    if (!isCurrent()) return
                    const sharedSnapshots = snapshots.filter(snapshot => !isCurrentState(snapshot.short_id))
                    const sorted = sortArray(sharedSnapshots, 'snid', 'descending', false)
                    snapshotId = sorted.length ? sorted[0].snid : null
                }
                if (isCurrent()) await this.createInstances(snapshotId, true)
            } catch (error) {
                if (isCurrent()) this.distributedFetchingError = true
            } finally {
                if (isCurrent()) this.inviting = false
            }
        },
        createInvitationGenerator: function () {
            this.creatingGenerator = true
            const validityDateTodayPlusThreeMonths = new Date()
            validityDateTodayPlusThreeMonths.setMonth(validityDateTodayPlusThreeMonths.getMonth() + 3)
            // BE expects date format to have always 0 milliseconds
            validityDateTodayPlusThreeMonths.setMilliseconds(0)
            const validityDate = validityDateTodayPlusThreeMonths.toISOString()
            this.$axios
                .post('/invitations/create_single_instance_generator', {
                    sid: this.resolvedSpaceId,
                    init_name: 'LAST_IMMUTABLE_DISTRIBUTED_SNAPSHOT',
                    validity_timestamp: validityDate
                })
                .then(response => {
                    if (response.data.is_valid) {
                        this.invalidGenerator = false
                        this.invgens = response.data
                    }
                    if (!response.data.is_valid && !isDateGreaterThanToday(response.data.validity_timestamp)) {
                        this.invalidGenerator = true
                        this.errorContent = 'This invitation generator has expired. Please contact your lecturer or TA to get a valid invitation'
                    } else if (!response.data.is_valid) {
                        this.invalidGenerator = true
                        this.errorContent = 'This invitation generator has been exhausted. Please contact your lecturer or TA to get an invitation'
                    }
                })
                .catch(error => {
                    const snackBarText = getBackendErrorMessage(error, 'Could not create invitation generator, please try again later.')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 10000,
                            snackBarIcon: 'error'
                        })
                    }
                })
                .finally(() => {
                    this.creatingGenerator = false
                })
        },
        validateEmailsAndInvite: function () {
            if (this.emailsInput.split('@').length - 1 > 10) {
                this.showEmailWarning = true
            } else {
                this.skipDialog()
            }
        },
        closeDialog() {
            this.showEmailWarning = false
        },
        skipDialog() {
            this.showEmailWarning = false
            this.distributedFetchingError = false
            this.validateEmails(this.emailList).finally(() => {
                if (!this.emailsValidationError && !this.backendError && !this.foundInvalidEmails) {
                    this.inviteUsers(true)
                }
            })
        }
    },
    computed: {
        ...mapGetters('spaceStore', { routeSpaceArchived: 'isSpaceArchived' }),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        isSpaceArchived() {
            return this.spaceArchived === null ? this.routeSpaceArchived : this.spaceArchived
        },
        showInvitationErrorsDialog() {
            if (this.foundInvalidEmails || this.backendError || this.emailsValidationError || this.distributedFetchingError) {
                return true
            }
            return false
        },
        validationErrorType() {
            if (this.distributedFetchingError) {
                return this.DISTRIBUTED_SNAPSHOTS_FETCHING_ERROR
            } else if (this.foundInvalidEmails) {
                return this.emailValidationErrorTypes.INVALID_EMAILS_ERROR
            } else if (this.backendError || this.emailsValidationError) {
                return this.emailValidationErrorTypes.EMAIL_VALIDATION_BACKEND_ERROR
            }
            return null
        },
        firstInvGen() {
            if (this.invgens) {
                return document.location.origin + this.$router.resolve({ name: 'signup-index', params: { space: 'class', token: this.invgens.token } }).href
            } else {
                return 'No Invitation URL for this space'
            }
        }
    },
    mounted() {
        this.$axios.post(`/spaces/${this.resolvedSpaceId}/invgens`, { invitation_generator_type: 'SINGLE_INSTANCE' }).then(response => {
            const sortedGenerators = sortArray(response.data, 'igid', 'descending', false)
            if (sortedGenerators.length && sortedGenerators[0].is_valid) {
                this.invgens = sortedGenerators[0]
            } else if (sortedGenerators.length && !sortedGenerators[0].is_valid && !isDateGreaterThanToday(sortedGenerators[0].validity_timestamp)) {
                this.invalidGenerator = true
                this.errorContent = 'This invitation generator has expired. Please contact your lecturer or TA to get a valid invitation'
            } else if (sortedGenerators.length && !sortedGenerators[0].is_valid) {
                this.invalidGenerator = true
                this.errorContent = 'This invitation generator has been exhausted. Please contact your lecturer or TA to get an invitation'
            }
        })
        this.emitInviteActionState()
    },
    beforeDestroy() {
        this.invitationSourceRequest += 1
    },
    watch: {
        resolvedSpaceId() {
            this.invitationSourceRequest += 1
            this.inviting = false
            this.distributedFetchingError = false
        },
        instanceInvitationMode() {
            this.emitInviteActionState()
        },
        validMultipleInstances() {
            this.emitInviteActionState()
        },
        emailsInput() {
            this.emitInviteActionState()
        },
        inviting() {
            this.emitInviteActionState()
        },
        validatingEmails() {
            this.emitInviteActionState()
        }
    }
}
</script>
